import ActionButton from '@/components/Reusable/Button/ActionButton';
import MinimalButton from '@/ui/ComponentLibrary/Buttons/MinimalButton/MinimalButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Form, Formik } from 'formik';
import Image from 'next/image';
import { MFA_METHOD_SMS, MFA_METHOD_TOTP } from '@/utils/constants/mfa';
import OtpInput from './OtpInput';

export default function MfaOtpEntry({
  onSubmit,
  resendOtp,
  loading,
  resendLoading,
  isVerifying,
  selectedMethod,
  onSelectDifferentMethod,
  signInHref = '/login',
}) {
  const theme = useTheme();

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
      {/* Icon + Heading + Description */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex' }}>
          <Image
            src='/images/ic-password.svg'
            alt='Password Icon'
            width={60}
            height={60}
            priority
          />
          <Box sx={{ ml: 2, mb: 3 }}>
            <Typography
              variant='h6'
              sx={{
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Multi-factor authentication (MFA)
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.4,
              }}
            >
              {selectedMethod === MFA_METHOD_TOTP
                ? 'Enter the 6-digit code from your authenticator app below and then click "Verify."'
                : `A 6-digit code has been sent to the ${
                    selectedMethod === MFA_METHOD_SMS
                      ? 'phone number you provided'
                      : 'email you provided'
                  }. Enter it below and then click "Verify."`}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Formik Form */}
      <Formik
        initialValues={{ otp: '' }}
        validate={(values) => {
          const errors = {};
          if (!values.otp || values.otp.length !== 6) {
            errors.otp = 'Please enter the 6-digit code.';
          }
          return errors;
        }}
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form>
            {/* “Enter code” label (mb:3 now) */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant='body2'
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                Enter code
              </Typography>
            </Box>

            {/* Six OTP boxes */}
            <Box sx={{ mb: 1 }}>
              <OtpInput
                value={values.otp}
                onChange={(val) => setFieldValue('otp', val)}
                disabled={loading || isSubmitting}
              />
              {errors.otp && touched.otp && (
                <Typography
                  variant='caption'
                  sx={{
                    color: 'error.main',
                    mt: 1,
                  }}
                >
                  {errors.otp}
                </Typography>
              )}
            </Box>

            {/* "Don't have a code? Resend code" - only show for non-TOTP methods */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 2,
              }}
            >
              {selectedMethod !== MFA_METHOD_TOTP && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      mr: 1,
                    }}
                  >
                    Don't have a code?
                  </Typography>
                  <MinimalButton
                    text='Resend code'
                    variant='text'
                    color='primary'
                    loading={resendLoading}
                    disabled={resendLoading || isVerifying || isSubmitting}
                    onClick={resendOtp}
                  />
                </Box>
              )}

              {onSelectDifferentMethod ? (
                <Tooltip
                  title='Select a different MFA method for this login'
                  arrow
                >
                  <Box component='span'>
                    <MinimalButton
                      text='Select MFA method'
                      variant='text'
                      color='primary'
                      disabled={isVerifying || isSubmitting}
                      onClick={onSelectDifferentMethod}
                    />
                  </Box>
                </Tooltip>
              ) : null}
            </Box>

            {/* "Verify" Button */}
            <ActionButton
              type='submit'
              loading={loading}
              disabled={
                loading ||
                resendLoading ||
                isSubmitting ||
                values.otp.length !== 6
              }
              sx={{ mb: 2 }}
            >
              Verify
            </ActionButton>

            {/* “← Return to login” link */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Box
                component='a'
                href={signInHref}
                role='link'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: theme.palette.text.secondary,
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                <ChevronLeftIcon
                  sx={{ fontSize: 20, mr: 0.5, verticalAlign: 'middle' }}
                />
                <Typography component='span' variant='body2'>
                  Return to sign in
                </Typography>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
